import { jsx } from 'react/jsx-runtime';
import { createContext, forwardRef, useMemo, useState } from 'react';
import { ButtonPrimitive } from '../button-primitive/button-primitive.js';
import { useIdProvider, useProvidedId } from '../hooks/id-provider.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { accept } from '../utils/event.js';

/**
 * CollapseContext is a context that stores the manager and state of the collapse.
 */
const CollapseContext = createContext(null);
const ControlledRoot = forwardRef(({ isOpen, toggle, setOpen, ...props }, ref) => {
    const value = useMemo(() => {
        // This is protected by the Controlled Collapse prop definition we have above
        const controls = { isOpen, toggle, setOpen };
        return {
            isOpen,
            toggle: isToggleCollapse(controls) ? controls.toggle : () => controls.setOpen(!isOpen),
        };
    }, [isOpen, setOpen, toggle]);
    return (jsx(CollapseContext.Provider, { value: value, children: jsx("div", { "data-fpl-component": "primitive", ...props, ref: ref }) }));
});
ControlledRoot.displayName = 'CollapsePrimitive.ControlledRoot';
const UncontrolledRoot = forwardRef(({ defaultOpen, ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const value = useMemo(() => ({ isOpen, toggle: () => setIsOpen(!isOpen) }), [isOpen]);
    return (jsx(CollapseContext.Provider, { value: value, children: jsx("div", { "data-fpl-component": "primitive", ...props, ref: ref }) }));
});
UncontrolledRoot.displayName = 'CollapsePrimitive.UncontrolledRoot';
/**
 * Collapse.Root is an unstyled (“headless”) component used to wrap a Collapse.Header and Collapse.Content.
 */
const Root = forwardRef((props, ref) => {
    const [id, Provider] = useIdProvider();
    return (jsx(Provider, { value: id, children: 'defaultOpen' in props ? (jsx(UncontrolledRoot, { "data-fpl-component": "primitive", ...props, ref: ref })) : (jsx(ControlledRoot, { "data-fpl-component": "primitive", ...props, ref: ref })) }));
});
Root.displayName = 'CollapsePrimitive.Root';
/**
 * Collapse.Header is an unstyled (“headless”) component used inside a Collapse.Root.
 * Wraps the Collapse.Label and Collapse.Trail components
 */
const Header = forwardRef((props, ref) => (jsx("div", { "data-fpl-component": "primitive", ...props, ref: ref })));
Header.displayName = 'CollapsePrimitive.Header';
/**
 * Collapse.Label is an unstyled (“headless”) button used inside a Collapse.Header.
 * If the user does not provide an onClick handler, the Collapse.Label will toggle the collapse.
 */
const Label = forwardRef((props, ref) => {
    const manager = useValidatedContext(CollapseContext, 'CollapseContext', 'Collapse.Root');
    const id = useProvidedId();
    return (jsx(ButtonPrimitive, { ...props, ref: ref, "aria-expanded": manager.isOpen, "aria-controls": id, onClick: (e) => {
            accept(e);
            if (props.onClick) {
                props.onClick(e);
            }
            else {
                manager.toggle();
            }
        } }));
});
Label.displayName = 'CollapsePrimitive.Label';
/**
 * Collapse.Trail is an unstyled (“headless”) component used inside a Collapse.Header.
 */
const Trail = forwardRef((props, ref) => (jsx("div", { "data-fpl-component": "primitive", ...props, ref: ref })));
Trail.displayName = 'CollapsePrimitive.Trail';
/**
 * Collapse.Content is an unstyled (“headless”) component used inside a Collapse.Root.
 */
const Content = forwardRef((props, ref) => {
    const id = useProvidedId();
    const { isOpen } = useValidatedContext(CollapseContext, 'CollapseContext', 'Collapse.Root');
    return jsx("div", { "data-fpl-component": "primitive", ...props, ref: ref, hidden: !isOpen, id: id });
});
Content.displayName = 'CollapsePrimitive.Content';
/**
 * Collapse.Group is an unstyled (“headless”) component used to group multiple Collapse.Roots.
 */
const Group = forwardRef((props, ref) => (jsx("div", { "data-fpl-component": "primitive", ...props, ref: ref, role: "group" })));
Group.displayName = 'CollapsePrimitive.Group';
function isToggleCollapse(x) {
    return !!(x.toggle && !x.setOpen);
}

export { CollapseContext, Content, Group, Header, Label, Root, Trail };
//# sourceMappingURL=collapse-primitive.js.map
